import { type ReactNode } from 'react'
import { Calendar, Lock, User as UserIcon } from 'lucide-react'
import { Badge, Modal } from '@/components/ui'
import { ACTIVITY_LABELS, type ActivityItem } from './activityFeed.types'
import { formatActivityDate } from './activityFeed.helpers'

/**
 * Detail-Modal fuer eine Aktivitaet — gemeinsam genutzt von interner
 * Ticket-Detail-Ansicht und Kundenportal.
 *
 * Standard-Body: Datum + Autor (Calendar-/User-Icon-Zeile), Meta-Block,
 * Content. Caller koennen ueber Slots erweitern:
 *   - `headerActions`: rechts neben dem Title (z. B. „Antworten" /
 *     „Bearbeiten"-Buttons in der internen Sicht)
 *   - `bodyOverride`: ersetzt den Standard-Body komplett (z. B. Edit-Form)
 *   - `children`: wird UNTER dem Standard-Body gerendert (z. B. Reply-Form)
 */
export interface ActivityDetailModalProps {
  item: ActivityItem | null
  onClose: () => void
  /** Wenn `true` und `item.internal === true`, wird im Header ein
   *  „Intern"-Badge gezeigt (nur intern relevant). */
  showInternalBadge?: boolean
  headerActions?: ReactNode
  bodyOverride?: ReactNode
  children?: ReactNode
  /** Optionale Render-Funktion fuer den Content-Block (z. B. Telefon-
   *  Nummern als `tel:`-Links). Default: `whitespace-pre-wrap` plain. */
  renderContent?: (content: string) => ReactNode
}

export function ActivityDetailModal({
  item,
  onClose,
  showInternalBadge = false,
  headerActions,
  bodyOverride,
  children,
  renderContent,
}: ActivityDetailModalProps) {
  const cfg = item ? ACTIVITY_LABELS[item.kind] : null
  const Icon = cfg?.Icon

  return (
    <Modal
      open={item != null}
      onOpenChange={(o) => { if (!o) onClose() }}
      size="lg"
      title={
        item ? (
          <span className="flex items-center gap-2">
            {cfg ? (
              <Badge variant={cfg.variant} size="sm">
                {Icon ? <Icon className="w-3 h-3" /> : null}
                {item.badgeLabel || cfg.label}
              </Badge>
            ) : null}
            {showInternalBadge && item.internal ? (
              <Badge variant="warning" size="sm">
                <Lock className="w-3 h-3" />
                Intern
              </Badge>
            ) : null}
            <span className="truncate">{item.title || cfg?.label}</span>
            {headerActions ? <span className="ml-auto flex items-center gap-1">{headerActions}</span> : null}
          </span>
        ) : ''
      }
    >
      {item ? (
        <div className="space-y-4">
          {bodyOverride ? (
            bodyOverride
          ) : (
            <>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-caption text-gray-500">
                {item.date ? (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatActivityDate(item.date, { long: true })}
                  </span>
                ) : null}
                {item.author ? (
                  <span className="inline-flex items-center gap-1">
                    <UserIcon className="w-3 h-3" />
                    {item.author}
                  </span>
                ) : null}
              </div>

              {Object.keys(item.meta).length > 0 ? (
                <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-small bg-gray-50 rounded-lg p-3 text-gray-700">
                  {Object.entries(item.meta).map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="font-medium text-gray-600">{k}:</dt>
                      <dd className="break-words">{v}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {item.content ? (
                <div className="whitespace-pre-wrap text-body text-gray-800 leading-relaxed">
                  {renderContent ? renderContent(item.content) : item.content}
                </div>
              ) : (
                <div className="text-small text-gray-400 italic">Kein Inhalt</div>
              )}
            </>
          )}

          {children ? <div className="border-t border-gray-200 pt-4">{children}</div> : null}
        </div>
      ) : null}
    </Modal>
  )
}
